import React, { FC, Dispatch, SetStateAction } from 'react';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

interface RadioOption {
  label: string;
  value: string;
  color?: string;
}

interface InspectionRadioGroupProps {
  label?: string;
  radioOptions: RadioOption[];
  value: any;
  setValue: Dispatch<SetStateAction<any>>;
}

const colorMap: Record<string, { border: string; bg: string; selectedBg: string; text: string }> = {
  good: { border: '#4caf50', bg: '#fff', selectedBg: '#4caf50', text: '#fff' },
  yes: { border: '#4caf50', bg: '#fff', selectedBg: '#4caf50', text: '#fff' },
  needs_attention: { border: '#ff9800', bg: '#fff', selectedBg: '#ff9800', text: '#fff' },
  requires_service: { border: '#f44336', bg: '#fff', selectedBg: '#f44336', text: '#fff' },
  no: { border: '#f44336', bg: '#fff', selectedBg: '#f44336', text: '#fff' },
  n_a: { border: '#9e9e9e', bg: '#fff', selectedBg: '#9e9e9e', text: '#fff' },
};

const defaultColors = { border: '#90a4ae', bg: '#fff', selectedBg: '#546E7A', text: '#fff' };

const InspectionRadioGroup: FC<InspectionRadioGroupProps> = ({ label, radioOptions, value, setValue }) => {
  const hasNaOption = radioOptions.some((opt) => opt.value === 'n_a');
  const isNaSelected = hasNaOption && value?.toString() === 'n_a';
  const visibleOptions = radioOptions.filter((opt) => opt.value !== 'n_a');

  const optionCount = visibleOptions.length;
  const gridXs = optionCount === 1 ? 12 : optionCount === 2 ? 6 : 4;

  return (
    <Grid container sx={{ display: 'flex', alignItems: 'center' }}>
      {label && (
        <Grid item xs={12} md={hasNaOption ? 3 : 4}>
          <Typography sx={{ fontWeight: 500, mb: { xs: 0.5, md: 0 } }}>{label}</Typography>
        </Grid>
      )}
      <Grid item xs={hasNaOption ? 10 : 12} md={label ? (hasNaOption ? 8 : 8) : 12}>
        <Grid container spacing={1}>
          {visibleOptions.map((opt) => {
            const isSelected = !isNaSelected && opt.value.toString() === value?.toString();
            const colors = colorMap[opt.value] || defaultColors;

            return (
              <Grid item xs={gridXs} key={opt.value}>
                <Button
                  variant={isSelected ? 'contained' : 'outlined'}
                  fullWidth
                  disabled={isNaSelected}
                  onClick={() => setValue(opt.value)}
                  sx={{
                    minHeight: { xs: 42, md: 36 },
                    fontSize: { xs: '11px', md: '13px' },
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    px: 0.5,
                    lineHeight: 1.2,
                    borderColor: isNaSelected ? '#ccc' : colors.border,
                    borderWidth: '2px',
                    color: isSelected ? colors.text : isNaSelected ? '#ccc' : colors.border,
                    bgcolor: isSelected ? colors.selectedBg : colors.bg,
                    '&:hover': {
                      borderColor: isNaSelected ? '#ccc' : colors.border,
                      borderWidth: '2px',
                      bgcolor: isSelected ? colors.selectedBg : `${colors.border}14`,
                    },
                    '&.Mui-disabled': {
                      borderColor: '#e0e0e0',
                      borderWidth: '2px',
                      color: '#ccc',
                    },
                  }}
                >
                  {opt.label}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Grid>
      {hasNaOption && (
        <Grid item xs={2} md={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isNaSelected}
                onChange={(e) => {
                  if (e.target.checked) {
                    setValue('n_a');
                  } else {
                    setValue('');
                  }
                }}
                size="small"
                sx={{ color: '#9e9e9e', '&.Mui-checked': { color: '#9e9e9e' } }}
              />
            }
            label={<Typography sx={{ fontSize: '12px', color: '#757575' }}>N/A</Typography>}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default InspectionRadioGroup;
